import { WeekDaysEnum } from "@oysterhr/schema"
import classNames from "classnames"
import { useTranslation } from "react-i18next"
import { WorkScheduleFormLegacy } from "./WorkScheduleFormLegacy"
import { Icon, Button, Text } from "@oysterhr/ui"
import { UseFormReturn } from "react-hook-form"
import { HireWorkDaysFormValues, WorkDaysFormValuesLegacy } from "./types"
import { DAYS } from "./constants"
import { HireWorkScheduleForm, HireWorkScheduleFormProps } from "./HireWorkScheduleForm"

type DaysSelectorProps = {
  selectedDays: string[]
  onDayClick: (day: WeekDaysEnum, selected: boolean) => void
  onClearAll?: () => void
  className?: string
}
export const DaysSelector = ({
  selectedDays,
  onDayClick,
  onClearAll,
  className,
}: DaysSelectorProps) => {
  const { t } = useTranslation("WorkScheduleComponents")

  return (
    <div className={classNames("flex items-center", className)}>
      {DAYS.map(day => {
        const selected = selectedDays.includes(day)

        return (
          <button
            key={day}
            type="button"
            className={classNames("mr-4 h-10 w-10 rounded-lg", {
              "bg-primary-base text-white": selected,
              "border border-gray-200 bg-gray-100 text-gray-400": !selected,
            })}
            id={`checkbox-${day}`}
            onClick={() => onDayClick(day, selected)}
          >
            {t(`weekdays.short.${day}`)}
          </button>
        )
      })}
      {onClearAll && (
        <Button onClick={onClearAll} variation="borderless" size="sm" type="button">
          <Text size="tiny" className="text-primary-base">
            {t("clearSelection")}
          </Text>
        </Button>
      )}
    </div>
  )
}

export type HireHoursSelectorProps = {
  selectedDays: string[]
  onCopyToAllClick: () => void
  form: UseFormReturn<HireWorkDaysFormValues>
} & Pick<HireWorkScheduleFormProps, "onWorkScheduleChange" | "isClearable">
export const HireHoursSelector = ({
  selectedDays,
  onCopyToAllClick,
  form,
  isClearable = true,
  onWorkScheduleChange,
}: HireHoursSelectorProps) => {
  const { t } = useTranslation("WorkScheduleComponents")
  const {
    formState: { errors },
  } = form

  return (
    <div className="flex items-start space-x-2">
      <div className="space-y-4">
        {selectedDays.map(day => {
          return (
            <HireWorkScheduleForm
              isClearable={isClearable}
              key={day}
              day={day as WeekDaysEnum}
              errors={errors.workDays?.[day as WeekDaysEnum]}
              form={form}
              onWorkScheduleChange={onWorkScheduleChange}
            />
          )
        })}
      </div>

      {selectedDays.length > 1 && (
        <Button
          onClick={onCopyToAllClick}
          variation="borderless"
          size="sm"
          type="button"
          startAdornment={<Icon name="Copy" size="sm" />}
        >
          <Text size="tiny" className="text-primary-base">
            {t("copyToAll")}
          </Text>
        </Button>
      )}
    </div>
  )
}

export type HoursSelectorLegacyProps = {
  selectedDays: string[]
  onCopyToAllClick: () => void
  form: UseFormReturn<WorkDaysFormValuesLegacy>
  onWorkScheduleChange?: (workDays: WorkDaysFormValuesLegacy["workDays"]) => void
  isClearable?: boolean
}
export const HoursSelectorLegacy = ({
  selectedDays,
  onCopyToAllClick,
  form,
  isClearable = true,
  onWorkScheduleChange,
}: HoursSelectorLegacyProps) => {
  const { t } = useTranslation("WorkScheduleComponents")
  const {
    formState: { errors },
  } = form

  return (
    <div className="flex items-start space-x-2">
      <div className="space-y-4">
        {selectedDays.map(day => {
          return (
            <WorkScheduleFormLegacy
              isClearable={isClearable}
              key={day}
              day={day as WeekDaysEnum}
              errors={errors.workDays?.[day as WeekDaysEnum]?.[0]}
              form={form}
              onWorkScheduleChange={onWorkScheduleChange}
            />
          )
        })}
      </div>

      {selectedDays.length > 1 && (
        <Button
          onClick={onCopyToAllClick}
          variation="borderless"
          size="sm"
          type="button"
          startAdornment={<Icon name="Copy" size="sm" />}
        >
          <Text size="tiny" className="text-primary-base">
            {t("copyToAll")}
          </Text>
        </Button>
      )}
    </div>
  )
}
